/**
 * Final CorelDRAW-oriented pass over a per-row composite production SVG.
 *
 * Corel imports `<g clip-path="url(#…)">` as a PowerClip — but only reliably
 * when the clipped group carries NO transform of its own and the clipPath
 * geometry is in the same (root) space. A clip written inside the composite's
 * flip/design transform chain came back mispositioned. So instead of clipping
 * in place, the composite is REBUILT:
 *
 *   <svg …>
 *     <defs> one clipPath per panel, root-space cut silhouette </defs>
 *     <g id="laser-outlines"> cut band + searching outline, under everything </g>
 *     <g id="panel-frontpanel" clip-path="url(#powerclip-1)">
 *       …the isolated piece: full wrapper chain pruned to this panel's artwork
 *          PLUS the names/numbers/logos that sit on it (ids namespaced)…
 *     </g>
 *     …one such group per panel…
 *   </svg>
 *
 * Every panel is therefore its own PowerClip with its overlays inside it, and
 * the Anchor layer never makes it into the file (it is pruned with the other
 * non-panel siblings). Falls back to anchor removal + outlines only when a
 * panel could not be isolated (windowed fallback pieces hold neighbour art).
 */
import type { OutlineSpec } from './nesting';
import { offsetPathData } from './offset';
import { namespaceSvgIds } from './logoVectorize';
import { sizeLabelPathMarkup, type GlyphFont } from './labelGlyphs';
import { sizeLabelMarkup, type PanelLabelPlacement } from './panelSplitter';
import { mmToPt } from './units';

export interface CompositeDims {
  viewBoxSize: number;
  widthPt: number;
  heightPt: number;
}

/** Per-panel size indicator, printed INSIDE each panel's PowerClip group. */
export interface CompositeLabelOptions {
  sizeText: string;
  fontPt: number;
  outlinePt: number;
  /**
   * OpenType font to outline the label with. Corel clips curves but leaves
   * `<text>` children outside the PowerClip, so with a font the label becomes
   * paths; without one it falls back to `<text>`.
   */
  font?: GlyphFont | null;
}

function labelMarkupFor(
  placement: PanelLabelPlacement,
  labels: CompositeLabelOptions | undefined,
  dims: CompositeDims
): string {
  const text = labels?.sizeText.trim();
  if (!text) return '';
  const ptPerUnitY = dims.heightPt / dims.viewBoxSize || 1;
  const fontUnits = labels!.fontPt / ptPerUnitY;
  const strokeUnits = labels!.outlinePt / (dims.widthPt / dims.viewBoxSize || 1);
  const { x, y } = placement.anchor;
  if (labels!.font) {
    const outlined = sizeLabelPathMarkup(labels!.font, x, y, text, fontUnits, strokeUnits);
    if (outlined) return outlined;
  }
  return sizeLabelMarkup(x, y, text, fontUnits, strokeUnits);
}

const r = (n: number) => Math.round(n * 1000) / 1000;

function outlineMarkup(d: string, widthUnits: number): string {
  return (
    `<path d="${d}" fill="none" stroke="#000000" stroke-width="${r(widthUnits)}"` +
    ` stroke-linejoin="round" stroke-linecap="round"/>`
  );
}

function findFlipMarkup(root: Element): Element | undefined {
  return Array.from(root.children).find(
    (el) =>
      el.tagName.toLowerCase() === 'g' &&
      /scale\(1,\s*-1\)/.test(el.getAttribute('transform') ?? '')
  );
}

function sanitizeId(value: string): string {
  return value.replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '') || 'panel';
}

/** Laser outlines for every isolated panel, in root/viewBox units. */
async function laserOutlinesMarkup(
  placements: PanelLabelPlacement[],
  spec: OutlineSpec,
  dims: CompositeDims
): Promise<string> {
  const ptPerUnit = dims.heightPt / dims.viewBoxSize || 1;
  const unitsPerMm = mmToPt(1) / ptPerUnit;
  let markup = '';
  for (const placement of placements) {
    const d = placement.outlineViewBoxD;
    if (!d || placement.piece.windowedFallback) continue;
    if (spec.searchEnabled) {
      // True offset curve, stroke only: nothing is filled between it and the panel.
      const offset = await offsetPathData(d, spec.searchOffsetMm * unitsPerMm, 0.2 / ptPerUnit);
      if (offset) markup += outlineMarkup(offset, spec.searchWidthMm * unitsPerMm);
    }
    // 2×width stroke: the artwork covers the inward half, leaving exactly
    // `width` going outwards.
    markup += outlineMarkup(d, 2 * spec.cutWidthMm * unitsPerMm);
  }
  return markup ? `<g id="laser-outlines">${markup}</g>` : '';
}

export async function finalizeCompositeForCorel(
  compositeSvg: string,
  placements: PanelLabelPlacement[],
  spec: OutlineSpec,
  dims: CompositeDims,
  labels?: CompositeLabelOptions
): Promise<string> {
  const open = compositeSvg.match(/<svg\b[^>]*>/i);
  if (!open || open.index === undefined) return compositeSvg;
  const closeIndex = compositeSvg.lastIndexOf('</svg>');
  if (closeIndex < 0) return compositeSvg;

  const outlines = await laserOutlinesMarkup(placements, spec, dims);

  const isolated = placements.filter(
    (p) => !p.piece.windowedFallback && !!p.outlineViewBoxD && !!p.piece.panel.sourceId
  );
  const allIsolated = isolated.length > 0 && isolated.length === placements.length;

  if (!allIsolated) {
    // Can't rebuild per panel: keep the composite, drop the Anchor, add outlines.
    const doc = new DOMParser().parseFromString(compositeSvg, 'image/svg+xml');
    if (doc.getElementsByTagName('parsererror').length) return compositeSvg;
    const root = doc.documentElement;
    const flip = findFlipMarkup(root);
    if (flip) {
      Array.from(flip.querySelectorAll('[id]')).forEach((el) => {
        if (/anchor/i.test(el.getAttribute('id') ?? '')) el.remove();
      });
    }
    const body = new XMLSerializer().serializeToString(root);
    const bodyOpen = body.match(/<svg\b[^>]*>/i)!;
    const bodyClose = body.lastIndexOf('</svg>');
    // Labels at the root (no per-panel groups to put them in).
    const rootLabels = placements.map((p) => labelMarkupFor(p, labels, dims)).join('');
    return (
      body.slice(0, bodyOpen.index! + bodyOpen[0].length) +
      outlines +
      body.slice(bodyOpen.index! + bodyOpen[0].length, bodyClose) +
      rootLabels +
      '</svg>'
    );
  }

  const clipDefs = isolated
    .map(
      (p, i) =>
        `<clipPath id="powerclip-${i + 1}" style="clip-rule:nonzero">` +
        `<path d="${p.outlineViewBoxD}"/></clipPath>`
    )
    .join('');

  const panelGroups = isolated
    .map((p, i) => {
      const name = sanitizeId(p.piece.panel.name.toLowerCase());
      // The piece's inner markup is the composite's wrapper chain pruned to this
      // panel + the overlays on it; namespacing keeps the per-panel copies of
      // fabric's defs/clip ids from colliding.
      const inner = namespaceSvgIds(p.piece.inner, `p${i + 1}x`);
      // The size indicator rides inside the panel's PowerClip: uppercase with
      // its baseline on the panel's bottom edge, so the clip leaves it whole.
      const label = labelMarkupFor(p, labels, dims);
      // Mirror CorelDRAW's own PowerClip export: the clip as a style property
      // on a group whose contents sit in one inner group.
      return (
        `<g id="panel-${name}" style="clip-path:url(#powerclip-${i + 1})">` +
        `<g>${inner}${label}</g></g>`
      );
    })
    .join('');

  return `${open[0]}<defs>${clipDefs}</defs>${outlines}${panelGroups}</svg>`;
}
